/**
 * Cambio Préstamo de Sucursal — migración web de ares8090 (escritorio).
 *
 * Mueve un préstamo activo a otra sucursal: el backend actualiza
 * prestamo, amort, factmot y abonos. NO mueve el vehículo del inventario
 * (igual que el escritorio — moverlo es una transferencia de chasis
 * aparte).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

export default function CambioSucursalPrestamo({ noprestInicial, enHub } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [prestamos, setPrestamos] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prestamo, setPrestamo] = useState(null);
  const [sucursal, setSucursal] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargarPrestamos = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/refinanciamiento_prestamos", {});
      if (ok) setPrestamos(data.prestamos || []);
      else
        showAlert({ message: data.Error || "No se pudieron cargar los préstamos", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  const cargarSucursales = async () => {
    const { ok, data } = await post("/api/config_catalogo_listar", { catalogo: "sucursal" });
    if (ok) setSucursales(data.filas || []);
    else
      showAlert({ message: data.Error || "No se pudieron cargar las sucursales", color: "error" });
  };

  useEffect(() => {
    cargarPrestamos();
    cargarSucursales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setPrestamo(null);
    setSucursal("");
    setConfirmOpen(false);
  };

  const validar = () => {
    if (!prestamo) {
      showAlert({ message: "Debe seleccionar el préstamo a mover", color: "warning" });
      return false;
    }
    if (!sucursal) {
      showAlert({ message: "Debe indicar la nueva sucursal", color: "warning" });
      return false;
    }
    if (sucursal === prestamo.sucursal) {
      showAlert({ message: "El préstamo ya pertenece a esa sucursal", color: "warning" });
      return false;
    }
    return true;
  };

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !prestamos.length) return;
    const opcion = prestamos.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) {
      setPrestamo(opcion);
      setSucursal("");
    }
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/cambiosucursalprestamo", {
        noprest: prestamo.noprest,
        sucursal,
      });
      if (ok) {
        showAlert({
          message:
            data.mensajeok || `Préstamo #${prestamo.noprest} movido a la sucursal ${sucursal}`,
          color: "success",
        });
        limpiar();
        cargarPrestamos();
      } else {
        showAlert({ message: data.Error || "No se pudo cambiar la sucursal", color: "error" });
        setConfirmOpen(false);
      }
    } finally {
      setGuardando(false);
    }
  };

  const chipInfo = {
    bgcolor: FP.color.surfaceAlt,
    color: FP.color.textSecondary,
    fontWeight: 700,
  };

  const descSucursal = (s) => {
    const f = sucursales.find((x) => x.sucursal === s);
    return f ? `${f.sucursal} — ${f.descripcion}` : s || "—";
  };

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title="Cambio Préstamo de Sucursal"
        subtitle="Mueve el préstamo y sus movimientos a otra sucursal — el vehículo del inventario no se mueve (ares8090)"
        cta={
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Préstamo a mover */}
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          <SectionTitle icon={SearchOutlinedIcon} color={FP.color.accentBlue}>
            Préstamo a Mover
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => {
                  setPrestamo(v);
                  setSucursal("");
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.noprest} — {o.nombres}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {prestamo && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={prestamo.nombres || "—"} sx={chipInfo} />
                  <Chip label={`Cédula ${prestamo.cedula || "—"}`} sx={chipInfo} />
                  {prestamo.chasis && <Chip label={`Chasis ${prestamo.chasis}`} sx={chipInfo} />}
                  <Chip
                    label={`Sucursal actual: ${descSucursal(prestamo.sucursal)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Nueva sucursal */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={StorefrontOutlinedIcon} color={FP.color.success}>
            Nueva Sucursal
          </SectionTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                label="Nueva sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                disabled={!prestamo}
                sx={fpInputSx}
              >
                {sucursales.map((s) => (
                  <MenuItem key={s.sucursal} value={s.sucursal}>
                    {s.sucursal} — {s.descripcion}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={8}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Button
                startIcon={
                  guardando ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SwapHorizOutlinedIcon />
                  )
                }
                onClick={() => {
                  if (validar()) setConfirmOpen(true);
                }}
                disabled={guardando || !prestamo}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Cambiar sucursal
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Confirmación */}
      <Dialog
        open={confirmOpen}
        onClose={() => !guardando && setConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <WarningAmberOutlinedIcon sx={{ color: FP.color.warning }} />
          Confirmar cambio de sucursal
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", mb: 1.5 }}>
            El préstamo <b>#{prestamo?.noprest}</b> de <b>{prestamo?.nombres}</b> pasará de la
            sucursal <b>{descSucursal(prestamo?.sucursal)}</b> a <b>{descSucursal(sucursal)}</b>,
            junto con su amortización, facturas y abonos.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: "0.85rem", color: FP.color.textMuted }}>
            El vehículo del inventario NO se mueve — si también debe cambiar de sucursal, haga la
            transferencia de chasis por separado.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={guardando} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              guardando ? <CircularProgress size={16} color="inherit" /> : <SwapHorizOutlinedIcon />
            }
            onClick={guardar}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Sí, cambiar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
